import { useEffect, useState, useContext, useMemo } from "react";
import {
  Box,
  Button,
  TextField,
  Paper,
  Toolbar,
  Typography,
  Stack,
  useTheme,
  InputAdornment,
} from "@mui/material";
import { Plus, SearchX, ArrowLeft } from "lucide-react";
import { AuthContext } from "../../context/AuthContext";
import SearchIcon from "@mui/icons-material/Search";
import {
  getAllRolesApi,
  createRoleApi,
  updateRoleApi,
  deleteRoleApi,
} from "../../api/roles.api";
import RolesTable from "./RolesTable";
import RoleFormModal from "./RoleFormModal";
import AccessDenied from "../AccessDenied";
import PageTitle from "../../components/PageTitle";
import NoRolesFound from "./NotFound";

export default function Roles() {
  const theme = useTheme();
  const { permissions, user, loading: userLoading } = useContext(AuthContext);

  const canView = permissions.includes("management.view");
  const canCreate = permissions.includes("management.create");
  const canUpdate = permissions.includes("management.update");
  const canDelete = permissions.includes("management.delete");

  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [search, setSearch] = useState("");
  const [currentUserRole, setCurrentUserRole] = useState("");

  const fetchRoles = async () => {
    if (!canView) {
      setRolesLoading(false);
      return;
    }
    const r = await getAllRolesApi();
    setRoles(r.data.data || []);
    setRolesLoading(false);
  };

  useEffect(() => {
    if (!user) return;
    setCurrentUserRole(user.roles?.[0]?.name || "");
    fetchRoles();
  }, [user]);

  const filteredRoles = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return roles;
    return roles.filter((role) => role?.name?.toLowerCase().includes(q));
  }, [roles, search]);

  if (!userLoading && !canView) {
    return <AccessDenied message="You do not have permission to view Roles." />;
  }

  return (
    <Box px={{ xs: 3, md: 2 }}>
      <Paper
        elevation={4}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 8px 30px rgba(16,24,40,0.08)",
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: { xs: 2, md: 3 },
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.6), rgba(255,255,255,0.35))",
          }}
        >
          <PageTitle parts={["Roles"]} />
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              placeholder="Search roles..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: { xs: "100%", sm: 260, md: 300 },
                bgcolor: "#fff",
                borderRadius: 3,
                px: 1,
                boxShadow: "inset 0 1px 0 rgba(0,0,0,0.03)",
                "& fieldset": { border: "none" },
              }}
            />

            {canCreate && (
              <Button
                onClick={() => {
                  setEditingRole(null);
                  setOpenModal(true);
                }}
                variant="contained"
                startIcon={<Plus size={16} />}
                sx={{
                  px: { xs: 2, sm: 3 },
                  py: { xs: 0.75, sm: 1 },
                  fontWeight: 800,
                  fontSize: { xs: 13, sm: 14 },
                  borderRadius: 2,
                  background: `linear-gradient(90deg, ${theme.avatarGradients_1.avatarStart} 0%, ${theme.avatarGradients_1.avatarCenter} 50%, ${theme.avatarGradients_1.avatarEnd} 100%)`,
                  color: "#fff",
                  boxShadow: "0 10px 30px rgba(138,43,226,0.14)",
                }}
              >
                Add Role
              </Button>
            )}
          </Stack>
        </Toolbar>

        <Box>
          {!rolesLoading && filteredRoles.length === 0 ? (
            <NoRolesFound />
          ) : (
            <RolesTable
              roles={filteredRoles}
              loading={rolesLoading}
              canUpdate={canUpdate}
              canDelete={canDelete}
              currentUserRole={currentUserRole}
              onEdit={(role) => {
                if (!canUpdate) return;
                setEditingRole(role);
                setOpenModal(true);
              }}
              onDelete={async (role) => {
                if (!canDelete) return;
                await deleteRoleApi(role._id);
                fetchRoles();
              }}
            />
          )}
        </Box>
      </Paper>

      <RoleFormModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        role={editingRole}
        currentUserRole={currentUserRole}
        onSave={async (form) => {
          if (editingRole) {
            if (!canUpdate) return;
            await updateRoleApi(editingRole._id, form);
          } else {
            if (!canCreate) return;
            await createRoleApi(form);
          }
          setOpenModal(false);
          setEditingRole(null);
          fetchRoles();
        }}
      />
    </Box>
  );
}
